import { Event, Prisma } from "@prisma/client";
import { DateTime } from "luxon";
import * as time from "./time";

export const createGoogleMapsUrl = (venue: {
    googleMapsPlaceId?: string | null;
    googleMapsPlaceName?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    locationName?: string | null;
}) => {
    // If we have a Google Place ID, use it (most reliable)
    if (venue.googleMapsPlaceId) {
        return `https://www.google.com/maps/place/?q=place_id:${venue.googleMapsPlaceId}`;
    }

    // If we have coordinates and a place name, use both
    if (venue.latitude && venue.longitude && venue.googleMapsPlaceName) {
        const encodedName = encodeURIComponent(venue.googleMapsPlaceName);
        return `https://www.google.com/maps/search/${encodedName}/@${venue.latitude},${venue.longitude},17z`;
    }

    // If we only have coordinates, use them
    if (venue.latitude && venue.longitude) {
        return `https://www.google.com/maps/search/?api=1&query=${venue.latitude},${venue.longitude}`;
    }

    // Last resort: search by location name
    if (venue.locationName || venue.googleMapsPlaceName) {
        const searchQuery = encodeURIComponent(
            venue.locationName || venue.googleMapsPlaceName || "",
        );
        return `https://www.google.com/maps/search/?api=1&query=${searchQuery}`;
    }

    // Fallback to a generic maps URL if we have nothing else
    return "https://www.google.com/maps";
};

export const isValidVenue = (
    event: Prisma.EventGetPayload<{
        select: {
            googleMapsPlaceTypes: true;
            hostHasConfirmedIsValidLocation: true;
            isVirtual: true;
        };
    }>,
) => {
    if (event.isVirtual) return true;
    if (event.hostHasConfirmedIsValidLocation) return true;
    return isValidPlaceType(event.googleMapsPlaceTypes);
};

export const isValidPlaceType = (placeTypes: string[]) => {
    const mandatoryTypes = ["point_of_interest", "establishment"];
    if (mandatoryTypes.every((t) => placeTypes.includes(t))) {
        return true;
    }
    return false;
};

export const hasEndedUX = (
    event: Prisma.EventGetPayload<{
        select: {
            startTime: true;
            endTime: true;
            timezone: true;
            isTemplate: true;
        };
    }>,
) => {
    if (event.isTemplate) return false;

    // add 1h to start time in case there's no endTime
    // to allow last-minute RSVPs (at the event)
    let endTime = event.endTime;
    let startTime = DateTime.fromJSDate(event.startTime, {
        zone: "UTC",
    })
        .plus({ hours: 1 })
        .toJSDate();

    // If we have an end time, use halfway point between start and end
    // to calculate if the event has ended
    if (endTime) {
        const start = DateTime.fromJSDate(event.startTime);
        const end = DateTime.fromJSDate(endTime);
        // use safe end
        const duration = DateTime.max(start, end)
            .diff(start)
            .as("milliseconds");
        endTime = start.plus({ milliseconds: duration / 2 }).toJSDate();
    }

    return time.hasEnded(startTime, endTime, event.timezone);
};

export const isPaidEvent = (
    event: Prisma.EventGetPayload<{
        select: { stripeProductId: true; eventTicketPackage: true };
    }>,
    selectedTier: Prisma.EventTicketTierGetPayload<{
        select: { stripeProductId: true; isFree: true; priceDollars: true };
    }> | null,
) => {
    if (!!event.stripeProductId) return true;

    if (!!event.eventTicketPackage) {
        if (
            selectedTier?.isFree === false &&
            !!selectedTier?.priceDollars &&
            selectedTier?.priceDollars > 0 &&
            !!selectedTier?.stripeProductId
        ) {
            return true;
        }
    }

    return false;
};

// for backwards compatibility with old events page protected location fields
export const parseProtectedFields = ({
    isProtected,
    location,
}: {
    isProtected: boolean;
    location?: string;
}) => {
    if (!isProtected) return [];

    return [
        {
            name: "location",
            value: location,
        },
        {
            name: "map",
            value: undefined,
        },
    ];
};
